/**
 * SubscriptionBadge Component
 * 
 * Displays user's subscription status with:
 * - Active subscriber badge (green)
 * - Not subscribed with CTA button
 * - Subscription management link
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Crown, ExternalLink, AlertCircle, Check, Loader2 } from 'lucide-react';
import {
    getSubscription,
    createSubscriptionCheckout,
    SubscriptionInfo,
    isSubscriptionActive,
    formatSubscriptionStatus
} from '../services/subscriptionApiClient';
import toast from 'react-hot-toast';

interface SubscriptionBadgeProps {
    variant?: 'full' | 'compact' | 'minimal';
    showManageButton?: boolean;
    className?: string;
}

const SubscriptionBadge: React.FC<SubscriptionBadgeProps> = ({
    variant = 'compact',
    showManageButton = true,
    className = ''
}) => {
    const [loading, setLoading] = useState(true);
    const [subscribing, setSubscribing] = useState(false);
    const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
    const [hasSubscription, setHasSubscription] = useState(false);

    useEffect(() => {
        loadSubscription();
    }, []);

    const loadSubscription = async () => {
        try {
            setLoading(true);
            const response = await getSubscription();
            setHasSubscription(response.hasSubscription);
            setSubscription(response.subscription);
        } catch (error) {
            console.error('Failed to load subscription:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubscribe = async () => {
        try {
            setSubscribing(true);
            const response = await createSubscriptionCheckout();

            // Redirect to LemonSqueezy checkout
            window.location.href = response.checkoutUrl;
        } catch (error: any) {
            console.error('Failed to create checkout:', error);
            toast.error(error.message || 'Failed to start subscription checkout');
            setSubscribing(false);
        }
    };

    const handleManageSubscription = () => {
        if (subscription?.customerPortalUrl) {
            window.open(subscription.customerPortalUrl, '_blank');
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className={`flex items-center gap-2 text-zinc-400 ${className}`}>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading...</span>
            </div>
        );
    }

    const isActive = subscription && isSubscriptionActive(subscription.status);

    // === MINIMAL VARIANT ===
    if (variant === 'minimal') {
        if (isActive) {
            return (
                <div className={`flex items-center gap-1.5 ${className}`}>
                    <Crown className="w-4 h-4 text-amber-400" />
                    <span className="text-xs font-medium text-amber-400">Pro</span>
                </div>
            );
        }
        return null;
    }

    // === COMPACT VARIANT ===
    if (variant === 'compact') {
        if (isActive) {
            return (
                <div className={`flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg ${className}`}>
                    <Crown className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-medium text-amber-300">Starter Plan</span>
                    {showManageButton && subscription?.customerPortalUrl && (
                        <button
                            onClick={handleManageSubscription}
                            className="p-1 hover:bg-white/10 rounded transition-colors"
                            title="Manage subscription"
                        >
                            <ExternalLink className="w-3 h-3 text-amber-400/70" />
                        </button>
                    )}
                </div>
            );
        }

        return (
            <motion.button
                onClick={handleSubscribe}
                disabled={subscribing}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium text-sm shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
            >
                {subscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <Crown className="w-4 h-4" />
                )}
                <span>{subscribing ? 'Loading...' : 'Subscribe $15/mo'}</span>
            </motion.button>
        );
    }

    // === FULL VARIANT ===
    if (isActive) {
        const renewsAt = subscription?.renewsAt ? new Date(subscription.renewsAt) : null;
        const daysUntilRenewal = renewsAt ? Math.ceil((renewsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;

        return (
            <div className={`p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl ${className}`}>
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg">
                            <Crown className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-white">Starter Plan</h3>
                                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full flex items-center gap-1">
                                    <Check className="w-3 h-3" />
                                    Active
                                </span>
                            </div>
                            <p className="text-sm text-zinc-400 mt-0.5">
                                {daysUntilRenewal !== null && daysUntilRenewal > 0
                                    ? `Renews in ${daysUntilRenewal} day${daysUntilRenewal !== 1 ? 's' : ''}`
                                    : 'Renewing soon'
                                }
                            </p>
                        </div>
                    </div>

                    {showManageButton && subscription?.customerPortalUrl && (
                        <button
                            onClick={handleManageSubscription}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-amber-400 hover:text-amber-300 hover:bg-white/5 rounded-lg transition-colors"
                        >
                            <span>Manage</span>
                            <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>

                <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-4 text-xs text-zinc-500">
                    <span>$15.00/month</span>
                    <span>•</span>
                    <span>+15 credits/month</span>
                </div>
            </div>
        );
    }

    // Not subscribed - show CTA
    return (
        <div className={`p-4 bg-gradient-to-r from-zinc-800/50 to-zinc-700/50 border border-zinc-600/50 rounded-xl ${className}`}>
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-zinc-700 rounded-lg">
                        <Crown className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-white">Upgrade to Starter</h3>
                        <p className="text-sm text-zinc-400 mt-0.5">Get 20 credits now + 15 monthly</p>
                    </div>
                </div>

                <motion.button
                    onClick={handleSubscribe}
                    disabled={subscribing}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold text-sm shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all disabled:opacity-50"
                >
                    {subscribing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <>
                            <Crown className="w-4 h-4" />
                            <span>$15/month</span>
                        </>
                    )}
                </motion.button>
            </div>

            <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-3 text-xs text-zinc-400">
                <div className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-emerald-400" />
                    <span>20 welcome credits</span>
                </div>
                <div className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-emerald-400" />
                    <span>15 credits/month</span>
                </div>
                <div className="flex items-center gap-1">
                    <Check className="w-3 h-3 text-emerald-400" />
                    <span>Cancel anytime</span>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionBadge;
